import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Container,
    Paper,
    Chip,
    Stack,
    Snackbar,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    OutlinedInput,
    IconButton,
    Tooltip,
    styled,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions, Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import PauseIcon from '@mui/icons-material/Pause';
import AddIcon from '@mui/icons-material/Add';
import PublishIcon from '@mui/icons-material/Publish';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import MainLayoutDemand from "../MainLoyout/MainLayoutDemand";
import Api from "../../Services/Api";
import { useSelector } from "react-redux";
import {PlayArrow} from "@mui/icons-material";
import EditKeywordsDialog from "./EditKeywords";
import VisibilityIcon from '@mui/icons-material/Visibility';
import FeedInfoDialog from './FeedInfoDialog';
import StatsPanel from './StatsPanel';
import PublishFeedModal from './PublishFeedModal';

const CodeSnippet = styled('span')(({ theme }) => ({
    backgroundColor: theme.palette.grey[200],
    padding: theme.spacing(0.5, 1),
    borderRadius: '4px',
    fontFamily: 'monospace',
    display: 'inline-block',
}));

const FeedList = () => {
    const [feeds, setFeeds] = useState([]);
    const [filteredFeeds, setFilteredFeeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState(['Pending', 'Active']);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [openCapModal, setOpenCapModal] = useState(false);
    const [selectedFeedId, setSelectedFeedId] = useState(null);
    const [capValue, setCapValue] = useState('1000'); // Default value for cap
    const [openPublishModal, setOpenPublishModal] = useState(false);
    const [keywordsList, setKeywordsList] = useState([]);
    const [openPauseModal, setOpenPauseModal] = useState(false);
    const [pauseFeedId, setPauseFeedId] = useState(null); // Store the feed ID to pause
    const [toggleAction, setToggleAction] = useState(''); // Action: "pause" or "resume"
    const [openToggleModal, setOpenToggleModal] = useState(false); // Toggle modal visibility

    // New states for editing keywords
    const [openEditKeywords, setOpenEditKeywords] = useState(false);
    const [editKeywordFeed, setEditKeywordFeed] = useState(null);

    const [keywordLists, setKeywordLists] = useState([]);

    const [openFeedInfo, setOpenFeedInfo] = useState(false);
    const [selectedFeed, setSelectedFeed] = useState(null);

    const [openResumeModal, setOpenResumeModal] = useState(false); // New state for resume dialog

    const [stats, setStats] = useState({ types: {}, countries: {} });

    const navigate = useNavigate();

    const userRole = useSelector((state) => state.users.role);
    const isAdmin = useSelector((state) => state.users.is_admin);

    const capOptions = [1000, 3000, 5000, 10000, 15000, 20000, 25000, 30000, 40000, 50000]; // Cap options for dropdown

    useEffect(() => {
        const fetchFeeds = async () => {
            try {
                const response = await Api.listSpinSupplyFeeds();
                const feedsData = response || [];
                setFeeds(feedsData);
                setFilteredFeeds(feedsData);
                setLoading(false);
            } catch (err) {
                setError('Failed to load feeds. Please try again.');
                setSnackbarMessage('Failed to load feeds. Please try again.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                setLoading(false);
            }
        };

        fetchFeeds();
    }, []);

    useEffect(() => {
        const filtered = feeds.filter((feed) => {
            const matchesSearch =
                searchQuery === '' || // No filter if searchQuery is empty
                feed.supply_feed_id?.toString().toLowerCase().includes(searchQuery) ||
                feed.identifier?.toLowerCase().includes(searchQuery) ||
                (isAdmin && feed.demand_user.name.toLowerCase().includes(searchQuery));

            const matchesStatus = statusFilter.length === 0 || statusFilter.includes(feed.status);

            return matchesSearch && matchesStatus;
        });

        setFilteredFeeds(filtered);
    }, [feeds, searchQuery, statusFilter]);

    useEffect(() => {
        const calculateStats = (feedsData) => {
            const newStats = {
                countries: {}
            };

            feedsData.forEach(feed => {
                if (feed.status !== 'Active') return;
                
                const cap = Number(feed.cap) || 0;
                const device = (feed.device || '').toLowerCase().trim();
                const country = (feed.country || 'Unknown').toUpperCase().trim();

                // Initialize country entry if needed
                if (!newStats.countries[country]) {
                    newStats.countries[country] = {
                        total: 0,
                        mobile: 0,
                        desktop: 0
                    };
                }

                // Update counts based on device type
                if (device === 'all') {
                    newStats.countries[country].mobile += cap;
                    newStats.countries[country].desktop += cap;
                    newStats.countries[country].total += cap * 2;
                } else if (device === 'mobile') {
                    newStats.countries[country].mobile += cap;
                    newStats.countries[country].total += cap;
                } else if (device === 'desktop') {
                    newStats.countries[country].desktop += cap;
                    newStats.countries[country].total += cap;
                } else {
                    console.warn('Unknown device type:', feed.device);
                }
            });

            return newStats;
        };

        if (feeds.length > 0) {
            setStats(calculateStats(feeds));
        }
    }, [feeds]);

    const fetchKeywordsList = async () => {
        try {
            const response = await Api.getKeywordLists(true);
            if (response && response.data) {
                setKeywordLists(response.data);
            } else {
                setKeywordLists([]);
            }
        } catch (err) {
            console.error('Error fetching keywords list:', err);
            setSnackbarMessage('Failed to load keywords list.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setKeywordLists([]);
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const handleToggle = (feedId, currentStatus) => {
        console.log(`Toggling feed ${feedId} with current status: ${currentStatus}`);
        setSelectedFeedId(feedId);
        setToggleAction(currentStatus === 'Active' ? 'pause' : 'resume'); // Determine action based on status
        setOpenToggleModal(true); // Open confirmation modal
    };

    const confirmToggleAction = async () => {
        try {
            if (toggleAction === 'pause') {
                console.log(`Pausing feed ${selectedFeedId}`);
                await Api.pauseSpinSupplyFeed(selectedFeedId); // Pause API call
                setSnackbarMessage(`Feed ${selectedFeedId} has been paused.`);
            } else {
                console.log(`Resuming feed ${selectedFeedId}`);
                await Api.resumeSpinSupplyFeed(selectedFeedId); // Resume API call
                setSnackbarMessage(`Feed ${selectedFeedId} has been resumed.`);
            }
            setSnackbarSeverity('success');
            refreshFeeds(); // Refresh feeds after toggle
        } catch (error) {
            console.error(`Failed to ${toggleAction} the feed:`, error);
            setSnackbarMessage(`Failed to ${toggleAction} the feed. Please try again.`);
            setSnackbarSeverity('error');
        } finally {
            setOpenToggleModal(false); // Close modal
            setSnackbarOpen(true); // Show snackbar
        }
    };

    const refreshFeeds = async () => {
        setLoading(true); // Show loading spinner while refreshing
        try {
            const response = await Api.listSpinSupplyFeeds();
            setFeeds(response || []);
            setFilteredFeeds(response || []);
            setLoading(false);
        } catch (err) {
            console.error('Error refreshing feeds:', err);
            setError('Failed to refresh feeds.');
            setSnackbarMessage('Failed to refresh feeds.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setLoading(false);
        }
    };

    const handlePause = (feedId) => {
        console.log(`Pausing feed with ID: ${feedId}`); // Debugging line
        setPauseFeedId(feedId); // Store the feed ID
        setOpenPauseModal(true); // Open confirmation modal
    };

    const handleResume = (feedId) => {
        console.log(`Resuming feed with ID: ${feedId}`); // Debugging line
        setPauseFeedId(feedId); // Store the feed ID
        setOpenResumeModal(true); // Open resume modal
    };

    const handleOpenIncreaseCapModal = (feedId) => {
        setSelectedFeedId(feedId);
        setOpenCapModal(true); // Open modal for cap increase
    };

    const handleCloseCapModal = () => {
        setOpenCapModal(false);
        setCapValue('1000'); // Reset cap value
    };

    const handleSubmitCapChange = async () => {
        if (!capValue) {
            setSnackbarMessage('Please select a valid cap value.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        try {
            const response = await Api.updateSpinSupplyFeedCap(selectedFeedId, { new_cap: capValue });
            setSnackbarMessage(`Cap increased to ${capValue} searches/day.`);
            setSnackbarSeverity('success');
            setOpenCapModal(false); // Close the modal
            refreshFeeds(); // Refresh the table after updating
        } catch (error) {
            console.error('Error increasing cap:', error);
            setSnackbarMessage('Failed to increase cap. Please try again.');
            setSnackbarSeverity('error');
        } finally {
            setSnackbarOpen(true); // Show the snackbar with the message
        }
    };

    const handlePublish = (feedId) => {
        setSelectedFeedId(feedId);
        fetchKeywordsList(); // Fetch keywords list when opening modal
        setOpenPublishModal(true);
    };

    const handleClosePublishModal = () => {
        setOpenPublishModal(false);
    };


    const confirmPause = async () => {
        try {
            await Api.pauseSpinSupplyFeed(pauseFeedId); // API call to pause the feed
            setSnackbarMessage(`Feed ${pauseFeedId} has been paused.`);
            setSnackbarSeverity('success');
            refreshFeeds(); // Refresh the table after pausing
        } catch (error) {
            console.error('Pause Error:', error);
            setSnackbarMessage('Failed to pause feed. Please try again.');
            setSnackbarSeverity('error');
        } finally {
            setOpenPauseModal(false); // Close modal
            setSnackbarOpen(true); // Show snackbar
        }
    };

    const handleClosePauseModal = () => {
        setOpenPauseModal(false);
        setPauseFeedId(null); // Reset feed ID
    };

    const handleKeywordsUpdated = async (feedId, newKeywords) => {
        await fetchKeywordsList(); // Refresh the keywords lists
        await refreshFeeds(); // Refresh the feeds to show the updated keyword list
    };

    const handleOpenFeedInfo = (feed) => {
        setSelectedFeed(feed);
        setOpenFeedInfo(true);
    };

    const handleCloseFeedInfo = () => {
        setOpenFeedInfo(false);
        setSelectedFeed(null);
    };

    const handleFeedUpdated = (feedId, updatedFields) => {
        // Update the feeds list to reflect the changes
        setFeeds(prevFeeds => 
            prevFeeds.map(feed => 
                feed.id === feedId 
                    ? { ...feed, ...updatedFields }
                    : feed
            )
        );
        
        // Also update the selected feed if it's the one being updated
        if (selectedFeed && selectedFeed.id === feedId) {
            setSelectedFeed(prevFeed => ({ ...prevFeed, ...updatedFields }));
        }
        
        // Show success message
        setSnackbarMessage('Feed updated successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value.toLowerCase());
    };

    const confirmResume = async () => {
        try {
            console.log(`Resuming feed with ID: ${pauseFeedId}`); // Debugging line

            // Call the API to resume the feed
            const response = await Api.resumeSpinSupplyFeed(pauseFeedId);

            if (response.error) {
                throw new Error(response.error);
            }

            setSnackbarMessage(`Feed ${pauseFeedId} has been resumed.`);
            setSnackbarSeverity('success');
            refreshFeeds(); // Refresh the table after resuming
        } catch (error) {
            console.error('Error resuming feed:', error);
            setSnackbarMessage('Failed to resume feed. Please try again.');
            setSnackbarSeverity('error');
        } finally {
            setOpenResumeModal(false); // Close the resume dialog
            setSnackbarOpen(true); // Show snackbar
        }
    };

    if (loading) {
        return (
            <MainLayoutDemand>
                <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
            </MainLayoutDemand>
        );
    }

    const CodeSnippet = styled('span')(({ theme }) => ({
        backgroundColor: theme.palette.grey[200],
        padding: theme.spacing(0.5, 1),
        borderRadius: '4px',
        fontFamily: 'monospace',
        display: 'inline-block',
        maxWidth: '250px', // Control max width
        overflow: 'hidden', // Hide overflowing text
        textOverflow: 'ellipsis', // Add ellipses for long text
        whiteSpace: 'nowrap', // Ensure it stays on one line
    }));

    return (
        <MainLayoutDemand>
            <Container maxWidth="xl" sx={{ mt: 4 }}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: '12px' }}>
                    {isAdmin && (
                        <StatsPanel 
                            countryCaps={stats.countries}
                            sx={{ mb: 4 }}
                        />
                    )}
                    <Stack direction="row" justifyContent="right" alignItems="center" spacing={1}>
                        <Chip
                            label="Fast Track VIP Access"
                            color="secondary"
                            icon={<FlashOnIcon />}
                            sx={{ fontSize: '1rem', fontWeight: 'bold', py: 1, px: 2 }}
                        />
                    </Stack>
                    <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
                        Feeds
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic' }}>
                        View all supply feeds that you've created. You can monitor the status and details here.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ flex: 1, minWidth: '250px' }}>
                            <TextField
                                label="Quick Search"
                                variant="outlined"
                                fullWidth
                                value={searchQuery}
                                onChange={handleSearchChange}
                            />
                        </Box>
                        <FormControl sx={{ flex: 1, minWidth: '250px' }}>
                            <InputLabel>Status Filter</InputLabel>
                            <Select
                                multiple
                                value={statusFilter}
                                onChange={(event) => setStatusFilter(event.target.value)}
                                input={<OutlinedInput label="Status Filter" />}
                                renderValue={(selected) => selected.join(', ')}
                            >
                                <MenuItem value="Pending">Pending</MenuItem>
                                <MenuItem value="Active">Active</MenuItem>
                                <MenuItem value="Inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate('/spin/create')}
                            sx={{ height: '56px', alignSelf: 'center', whiteSpace: 'nowrap' }}
                        >
                            Add New Feed
                        </Button>
                    </Box>
                    <TableContainer component={Paper} elevation={3} sx={{ borderRadius: '12px' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell><strong>User</strong></TableCell>
                                    <TableCell><strong>Feed ID</strong></TableCell>
                                    <TableCell><strong>Client ID</strong></TableCell>
                                    <TableCell><strong>Source</strong></TableCell>
                                    <TableCell><strong>Type</strong></TableCell>
                                    <TableCell><strong>Device</strong></TableCell>
                                    <TableCell><strong>Cap</strong></TableCell>
                                    <TableCell><strong>Created Date</strong></TableCell>
                                    <TableCell><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredFeeds.length > 0 ? (
                                    filteredFeeds.map((feed) => (
                                        <TableRow key={feed.id}>
                                            <TableCell>
                                                <Chip
                                                    label={feed.status ?? 'Unknown'}
                                                    color={
                                                        feed.status === 'Active'
                                                            ? 'success'
                                                            : feed.status === 'Pending'
                                                                ? 'warning'
                                                                : feed.status === 'Inactive'
                                                                    ? 'error'
                                                                    : undefined
                                                    }
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{feed.demand_user.name}</TableCell>
                                            <TableCell>{feed.supply_feed_id || 'N/A'}</TableCell>
                                            <TableCell>{feed.identifier || 'N/A'}</TableCell>
                                            <TableCell>{feed.source ? feed.source.charAt(0).toUpperCase() + feed.source.slice(1) : 'Unknown'}</TableCell>
                                            <TableCell>{feed.type || 'Unknown'}</TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={
                                                        feed.device === 'all' ? 'Mobile & Desktop' :
                                                        feed.device === 'mobile' ? 'Mobile Only' :
                                                        feed.device === 'desktop' ? 'Desktop Only' :
                                                        feed.device || 'N/A'
                                                    }
                                                    size="small"
                                                    color={
                                                        feed.device === 'all' ? 'primary' :
                                                        feed.device === 'mobile' ? 'info' :
                                                        feed.device === 'desktop' ? 'secondary' :
                                                        'default'
                                                    }
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>{feed.cap || 'N/A'}</TableCell>
                                            <TableCell>{feed.created_at ? new Date(feed.created_at).toLocaleDateString() : 'Unknown'}</TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5}>
                                                    {userRole === 'demand' && (
                                                        <>
                                                            <Tooltip title={feed.status === 'Active' ? 'Pause Feed' : 'Resume Feed'}>
                                                                <span>
                                                                    <IconButton
                                                                        onClick={() => feed.status === 'Active' ? handlePause(feed.id) : handleResume(feed.id)}
                                                                        color={feed.status === 'Active' ? 'warning' : 'success'}
                                                                    >
                                                                        {feed.status === 'Active' ? <PauseIcon /> : <PlayArrow />}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                            <Tooltip title="Increase Cap">
                                                                <span>
                                                                    <IconButton
                                                                        onClick={() => handleOpenIncreaseCapModal(feed.id)}
                                                                        color="primary"
                                                                        disabled={feed.status !== 'Active'}
                                                                    >
                                                                        <AddIcon />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                    {isAdmin && (
                                                        <Tooltip title="Publish Feed">
                                                            <IconButton onClick={() => handlePublish(feed.id)} color="success" disabled={feed.status == 'Active' || feed.status == "Inactive"}>
                                                                <PublishIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {feed.supply_feed_id && (
                                                        <Tooltip title="View Feed Info">
                                                            <IconButton onClick={() => handleOpenFeedInfo(feed)} color="primary">
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center">
                                            No feeds available.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Cap Increase Modal */}
                <Dialog open={openCapModal} onClose={handleCloseCapModal} maxWidth="sm" fullWidth>
                    <DialogTitle>Increase Cap</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1">Select the new daily cap for this feed:</Typography>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>Cap (Searches per Day)</InputLabel>
                            <Select
                                value={capValue}
                                onChange={(e) => setCapValue(e.target.value)}
                                label="Cap (Searches per Day)"
                            >
                                {capOptions.map((cap) => (
                                    <MenuItem key={cap} value={cap}>
                                        {cap.toLocaleString()} searches/day
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseCapModal} color="secondary">Cancel</Button>
                        <Button onClick={handleSubmitCapChange} variant="contained" color="primary">Confirm</Button>
                    </DialogActions>
                </Dialog>

                {/* Publish Modal */}
                <PublishFeedModal
                    open={openPublishModal}
                    onClose={handleClosePublishModal}
                    selectedFeedId={selectedFeedId}
                    feeds={feeds}
                    setFeeds={setFeeds}
                    keywordLists={keywordLists}
                    fetchKeywordsList={fetchKeywordsList}
                    refreshFeeds={refreshFeeds}
                    setSnackbarMessage={setSnackbarMessage}
                    setSnackbarSeverity={setSnackbarSeverity}
                    setSnackbarOpen={setSnackbarOpen}
                />
                
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
                <FeedInfoDialog
                    open={openFeedInfo}
                    onClose={handleCloseFeedInfo}
                    feed={selectedFeed}
                    keywordsList={keywordLists}
                    isAdmin={isAdmin}
                    onFeedUpdated={handleFeedUpdated}
                />
                <Dialog open={openPauseModal} onClose={handleClosePauseModal} maxWidth="sm" fullWidth>
                    <DialogTitle>Pause Feed</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1">Are you sure you want to pause this feed?</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClosePauseModal} color="secondary">Cancel</Button>
                        <Button onClick={confirmPause} variant="contained" color="primary">Confirm</Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={openResumeModal} onClose={() => setOpenResumeModal(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Resume Feed</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1">Are you sure you want to resume this feed?</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenResumeModal(false)} color="secondary">Cancel</Button>
                        <Button onClick={confirmResume} variant="contained" color="primary">Confirm</Button>
                    </DialogActions>
                </Dialog>
                <EditKeywordsDialog
                    open={openEditKeywords}
                    onClose={() => setOpenEditKeywords(false)}
                    feed={editKeywordFeed}
                    onKeywordsUpdated={handleKeywordsUpdated}
                    setSnackbarMessage={setSnackbarMessage}
                    setSnackbarSeverity={setSnackbarSeverity}
                    setSnackbarOpen={setSnackbarOpen}
                    keywordLists={keywordLists}
                />
            </Container>
        </MainLayoutDemand>
    );
};

export default FeedList;